import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { ImpersonationBanner } from './ImpersonationBanner';
import { DualNotificationBells } from './NotificationBell';

// Sales module navigation items
const salesNavItems = [
  { id: 'dashboard', label: 'Dashboard', path: '/sales/dashboard', icon: '📊' },
  { id: 'bookings', label: 'Bookings', path: '/sales/bookings', icon: '📋' },
  { id: 'new-booking', label: 'New Booking', path: '/sales/bookings/new', icon: '➕' },
  { id: 'reports', label: 'Reports', path: '/sales/reports', icon: '📄' },
  { id: 'mis-reports', label: 'MIS Reports', path: '/sales/mis-reports', icon: '📈' },
  { id: 'multi-reports', label: 'Multi Reports', path: '/sales/multi-reports', icon: '📝' },
  { id: 'stock', label: 'Stock', path: '/sales/stock-report', icon: '🚗' },
  { id: 'masters', label: 'Masters', path: '/sales/masters', icon: '⚙️' },
];

const moreNavItems = [
  { id: 'sales-rep-perf', label: 'Sales Rep Performance', path: '/sales/reports/sales-rep-performance', icon: '🎯' },
  { id: 'insurance-expiry', label: 'Insurance Expiry', path: '/sales/reports/insurance-expiry', icon: '🛡️' },
  { id: 'delivery', label: 'Delivery Report', path: '/sales/delivery-report', icon: '🚚' },
  { id: 'rto', label: 'RTO Report', path: '/sales/rto-report', icon: '📋' },
  { id: 'financier', label: 'Financier Report', path: '/sales/financier-report', icon: '💰' },
  { id: 'bulk-upload', label: 'Bulk Upload', path: '/sales/bulk-upload', icon: '📤' },
  { id: 'google-sheets', label: 'Google Sheets', path: '/sales/google-sheets', icon: '📊' },
];

export function BasicLayout({ children, user, onLogout, title }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [showMoreMenu, setShowMoreMenu] = React.useState(false);
  const [showMobileMenu, setShowMobileMenu] = React.useState(false);

  const isActive = (path) => {
    if (path === '/sales/bookings' && location.pathname.startsWith('/sales/bookings/')) {
      return !location.pathname.includes('/new');
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const allNavItems = [...salesNavItems, ...moreNavItems];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FFF9F5' }}>
      {/* Impersonation Banner - shows when admin is viewing as another user */}
      <ImpersonationBanner />
      
      {/* Subtle background decoration */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-20" style={{ background: 'radial-gradient(circle, #FFCAD4 0%, transparent 70%)' }}></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full opacity-20" style={{ background: 'radial-gradient(circle, #FFE5D9 0%, transparent 70%)' }}></div>
      </div>

      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-[#FFCAD4]/30 backdrop-blur-xl bg-white/90">
        <div className="max-w-full mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Mobile Menu Button */}
            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="lg:hidden p-2 rounded-lg text-gray-600 hover:bg-[#FFE5D9]/30"
              aria-label="Toggle menu"
            >
              {showMobileMenu ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>

            {/* Logo & Module */}
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/modules')}
                className="flex items-center gap-3 hover:opacity-80 transition-opacity"
              >
                <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-md" style={{ background: 'linear-gradient(135deg, #FFE5D9 0%, #F4ACB7 100%)' }}>
                  <span className="text-xl">🚗</span>
                </div>
                <div className="hidden sm:block">
                  <h1 className="text-lg font-bold text-gray-800">Sales Module</h1>
                  <p className="text-xs text-[#F4ACB7]">AutoBook Pro</p>
                </div>
              </button>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {salesNavItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => navigate(item.path)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
                    isActive(item.path)
                      ? 'text-[#F4ACB7] border border-[#F4ACB7]/30'
                      : 'text-gray-600 hover:text-[#F4ACB7] hover:bg-[#FFE5D9]/30'
                  }`}
                  style={isActive(item.path) ? { backgroundColor: 'rgba(244, 172, 183, 0.15)' } : {}}
                >
                  <span>{item.icon}</span>
                  {item.label}
                </button>
              ))}
              
              {/* More dropdown */}
              <div className="relative">
                <button
                  onClick={() => setShowMoreMenu(!showMoreMenu)}
                  className="px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:text-[#F4ACB7] hover:bg-[#FFE5D9]/30 flex items-center gap-2"
                >
                  <span>⋯</span>
                  More
                </button>
                
                {showMoreMenu && (
                  <>
                    <div className="fixed inset-0" onClick={() => setShowMoreMenu(false)}></div>
                    <div className="absolute right-0 top-full mt-2 w-48 bg-white border border-[#FFCAD4]/30 rounded-xl shadow-xl py-2 z-50">
                      {moreNavItems.map((item) => (
                        <button
                          key={item.id}
                          onClick={() => { navigate(item.path); setShowMoreMenu(false); }}
                          className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 ${
                            isActive(item.path)
                              ? 'bg-[#FFE5D9]/30 text-[#F4ACB7]'
                              : 'text-gray-600 hover:text-[#F4ACB7] hover:bg-[#FFE5D9]/20'
                          }`}
                        >
                          <span>{item.icon}</span>
                          {item.label}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </nav>

            {/* User Menu */}
            <div className="flex items-center gap-2 sm:gap-3">
              <DualNotificationBells />
              <div className="text-right hidden sm:block">
                <p className="text-sm text-gray-800 font-medium">{user?.name}</p>
                <p className="text-xs text-gray-500">{user?.email}</p>
              </div>
              <button
                onClick={() => navigate('/modules')}
                className="px-2 sm:px-3 py-2 bg-[#FFE5D9]/50 border border-[#FFCAD4]/30 text-gray-700 rounded-lg hover:bg-[#FFE5D9] transition-all text-xs sm:text-sm"
              >
                Switch
              </button>
              <button
                onClick={onLogout}
                className="px-2 sm:px-3 py-2 bg-red-50 border border-red-200 text-red-500 rounded-lg hover:bg-red-100 transition-all text-xs sm:text-sm"
              >
                Logout
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {showMobileMenu && (
          <div className="lg:hidden border-t border-[#FFCAD4]/30 bg-white">
            <div className="max-h-[60vh] overflow-y-auto py-2">
              {allNavItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => { navigate(item.path); setShowMobileMenu(false); }}
                  className={`w-full px-4 py-3 text-left text-sm flex items-center gap-3 ${
                    isActive(item.path)
                      ? 'bg-[#FFE5D9]/30 text-[#F4ACB7] font-medium'
                      : 'text-gray-600 hover:text-[#F4ACB7] hover:bg-[#FFE5D9]/20'
                  }`}
                >
                  <span className="text-lg">{item.icon}</span>
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </header>

      {/* Page Title Bar */}
      {title && (
        <div className="border-b border-[#FFCAD4]/20 bg-white/50">
          <div className="max-w-full mx-auto px-4 py-3">
            <h2 className="text-xl font-semibold text-gray-800">{title}</h2>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="relative z-10 max-w-full mx-auto px-4 py-6">
        {children}
      </main>
    </div>
  );
}

export default BasicLayout;
